import _ from 'lodash';

import apiFetch from '@scale/frontend-lib/api-fetch';
import { CustomerReviewStatus } from '@scale/shared/defaults';
import { AuditInfo, AuditTask } from '@scale/shared/types/dashboard/audit';
import { AuditTaskPagination } from '@scale/shared/types/endpoints/customer/audit/tasks';
import { autoproxyAttachment } from '@scale/shared/assignmentJson/legacy/assignment-normalizer';
import {
  AuditBulkUpdate,
  AuditBulkResponse,
  AuditBulkTaskResponse,
} from '@scale/shared/types/endpoints/customer/audit/bulk';
import { captureClientSideError } from '../errors';

interface Signal {
  aborted: boolean;
}

interface AuditParams {
  project: string;
  timeZone: string;
  completedAtStart?: string;
  completedAtEnd?: string;
}

interface AuditTaskParams extends AuditParams {
  nPerPage: number;
  pageNumber: number;
}

class AbortError extends Error {}

async function fetchWithBackoff<T>(
  signal: Signal,
  limit: number,
  backoff: (attempt: number) => number,
  fetcher: () => Promise<T>,
): Promise<T> {
  if (limit <= 0) {
    throw new TypeError(`fetchWithBackoff invalid limit ${limit}`);
  }
  for (let i = 0; i < limit - 1; i++) {
    if (signal.aborted) {
      throw new AbortError();
    }
    try {
      const response = await fetcher();
      return response;
    } catch (e) {
      await timeout(backoff(i + 1));
    }
  }
  return await fetcher();
}

const autoProxyAttachments = (task: AuditTask): AuditTask => {
  try {
    return autoproxyAttachment(task);
  } catch (e) {
    captureClientSideError(e);
    return task;
  }
};

function timeout(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

export const fetchAuditTasks = async (query: AuditTaskParams): Promise<AuditTaskPagination> => {
  const page = await apiFetch<AuditTaskPagination>(null, 'internal/audit/tasks', { query });

  const tasks = page.tasks.map(task => autoProxyAttachments(task));

  return { ...page, tasks };
};

export const auditBulkUpdate = async (params: AuditBulkUpdate): Promise<AuditBulkResponse> => {
  return apiFetch<AuditBulkResponse>(null, 'internal/audit/bulk', { method: 'POST', body: params });
};

export async function getAuditInfo(query: AuditParams): Promise<AuditInfo> {
  return apiFetch(null, 'internal/audit/info', { query });
}

export async function fetchAuditTasksParallel(
  signal: Signal,
  info: AuditInfo,
  query: AuditParams,
): Promise<$FIXME[]> {
  const taskFetchRequests = await Promise.all(
    _.range(0, Math.ceil(info.taskCount / info.nPerPage)).map(pageNumber => {
      return fetchWithBackoff(
        signal,
        3,
        (attempt: number) => attempt * attempt * 500,
        () => {
          return fetchAuditTasks({
            ...query,
            nPerPage: info.nPerPage,
            pageNumber,
          });
        },
      );
    }),
  ).catch<AuditTaskPagination[]>(error => {
    if (error.name === 'AbortError') {
      return [];
    } else {
      throw error;
    }
  });
  return _.flatMap(taskFetchRequests, pagination => pagination.tasks);
}

export const auditBulkUpdateParallel = async (
  tasks: AuditTask[],
  status: CustomerReviewStatus,
): Promise<AuditBulkTaskResponse[]> => {
  const batchSize = 500;

  const auditBulkResponses = await Promise.all(
    _.times(Math.ceil(tasks.length / batchSize), async (index: number) => {
      const taskUpdates = tasks.slice(batchSize * index, batchSize * (index + 1)).map(task => {
        return {
          id: task.id,
          status,
        };
      });
      const response = await auditBulkUpdate({
        taskUpdates,
      });
      return response;
    }),
  );

  return _.flatMap(auditBulkResponses, response => response.taskResponses);
};
